import { Box, Button, Grid, MenuItem, Stack, SvgIcon, Tab, Tabs, TextField, useMediaQuery, useTheme } from '@mui/material';
import { Plus, SearchSm } from '../../../../assets';
import MessagesSnackbar from '../../../../components/shared/MessagesSnackbar';
import PageArea from '../../../../components/shared/PageArea';
import PageTitle from '../../../../components/shared/PageTitle';
import { UserContext } from '../../../../components/shared/useUser';
import { Section } from '../../../../components/ui/Section';
import useLogError from '../../../../hooks/useLogError';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import AutoshipmentTemplateTable from './AutoshipmentTemplateTable';
import { AutoshipmentTemplatesFilterOptions, client, OrderType } from '../../../../app/services/api/orderManagementClient';
import { AutoshipTemplateModel } from '../../../../app/services/api/orderManagementClient';
import { PaginatedList } from '../../../../app/services/api/orderManagementClient';
import LoadingWheel from '../../../../components/ui/LoadingWheel';
import { AutoshipTemplateStatusEnum } from '../../enums/AutoshipmentTemplateStatusEnum';
import { AutoshipmentTemplateTabEnum } from '../../enums/AutoshipmentTemplateTabEnum';
import { OrderTypeTranslationMap } from '../../../../enums/OrderType';

const AutoshipmentTemplates = () => {
  const [paginatedData, setPaginatedData] = useState<PaginatedList<AutoshipTemplateModel>>();
  const [activeTab, setActiveTab] = useState<AutoshipmentTemplateTabEnum>(AutoshipmentTemplateTabEnum.ACTIVE);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [date, setDate] = useState('');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [orderBy, setOrderBy] = useState<string>('createdDate');
  const theme = useTheme();
  const { t } = useTranslation('autoshipment');
  const { hasPermissionTo, selectedStore, isAdmin } = useContext(UserContext);
  const hasWritePermissions = hasPermissionTo(['AutoshipmentWrite']) || isAdmin();
  const hasDeletePermissions = hasPermissionTo(['AutoshipmentDelete']);
  const navigate = useNavigate();
  const { logError } = useLogError();
  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.md}px)`);
  const [orderType, setOrderType] = useState(-1);
  const [orderTypes, setOrderTypes] = useState<OrderType[]>([]);
  const [templateSearch, setTemplateSearch] = useState('');
  const defaultSort = 'id';
  const defaultDirection = 'desc';
  const defaultPageSize = 10;
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  const handleSnackbarOpen = (message: string, severity: 'success' | 'error') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  useEffect(() => {
    if (paginatedData !== undefined) { 
      loadFilterData({
        name: templateSearch,
        statusId: activeTab,
        orderTypeId: orderType,
        sort: orderBy || defaultSort,
        direction: order || defaultDirection,
        pageNumber: page,
        pageSize: defaultPageSize,
      });
    }
  }, [page, order, orderBy]);

  useEffect(() => {
    setIsLoading(true);
    loadData();
  }, [activeTab]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: AutoshipmentTemplateTabEnum) => {
    setActiveTab(newValue);
    setPage(0);
  };

  const loadData = async () => {
    setIsLoading(true);
    await fetchOrderTypes();
    await loadFilterData({
      name: templateSearch,
      statusId: activeTab,
      orderTypeId: orderType,
      sort: defaultSort,
      direction: defaultDirection,
      pageNumber: page,
      pageSize: defaultPageSize,
    });
    setIsLoading(false);
  };

  const fetchOrderTypes = async () => {
    try {
      const orderTypesResponse = await client.getOrderTypes(selectedStore?.storeNumber);
      setOrderTypes(orderTypesResponse.data?.filter(x => x.id != 12) ?? []);
    } catch (error) {
      console.error(t('errorLoading'), error);
    }
  };

  const loadFilterData = async (options: AutoshipmentTemplatesFilterOptions) => {
    setIsLoading(true);
    try {
      const result = await client.getAutoshipTemplates(options, selectedStore?.storeNumber ?? '');

      if (result.success) {
        setPaginatedData(result.data);
      } else {
        logError(result.message);
      }
    } catch (error) {
      logError(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setDate('');
  }, [activeTab]);

  const callLoadDataFromAPI = (options: AutoshipmentTemplatesFilterOptions) => {
    loadFilterData(options);
  };

  const clearAll = () => {
    setDate('');
    setTemplateSearch('');
    setOrderType(-1);
    setPage(0);
    callLoadDataFromAPI({
      pageNumber: 0,
      pageSize: 10,
    } as AutoshipmentTemplatesFilterOptions);

    setPage(0);
  };
  
  const searchClick = () => {
    setPage(0);

    callLoadDataFromAPI({
      name: templateSearch,
      statusId: activeTab,
      orderTypeId: orderType,
      sort: defaultSort,
      direction: defaultDirection,
      pageNumber: 0,
      pageSize: defaultPageSize,
    });
  };

  const navigateToTemplate = (id: number) => {
    navigate(`/manage-autoshipments/template/${id}`);
  };

  const handleDelete = async (id: number) => {
    if(!hasDeletePermissions) {
      return;
    }
    try {
      setIsLoading(true);
      const result = await client.deleteTemplate(selectedStore?.storeNumber ?? '', id?.toString());
      
      if (result.success) {
        handleSnackbarOpen(t('templateDeleted'), 'success');
        await loadData();
      } else {
        logError(result.message);
        handleSnackbarOpen(t('Failed to delete template'), 'error');
      }
    } catch (error) {
      logError(error);
      handleSnackbarOpen(t('Failed to delete template'), 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleArchive = async (id: number) => {
    if(!hasWritePermissions) {
      return;
    }
    try {
      setIsLoading(true);
      const result = await client.archiveTemplate(selectedStore?.storeNumber ?? '', id.toString());
      
      if (result.success) {
        handleSnackbarOpen(t('templateArchived'), 'success');
        await loadData();
      } else {
        logError(result.message);
        handleSnackbarOpen(t('Failed to archive template'), 'error');
      }
    } catch (error) {
      logError(error);
      handleSnackbarOpen(t('Failed to archive template'), 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PageArea>
      <Box display="flex"
        width="100%"
        height="100%"
        flexDirection="column" >
        <Grid container>
          <Stack width='100%'
            direction={isMobile ? 'column' : 'row'}
            sx={{ justifyContent: 'space-between' }}>
            <Box width={isMobile ? '100%' : 'auto'}>
              <PageTitle subTitle={t('templateSubTitle')}>
                {t('templateTitle')}
              </PageTitle>
            </Box>
            {hasWritePermissions && (
              <Box width={isMobile ? '100%' : 'auto'}
                sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                <Button
                  variant="primary"
                  size="lg"
                  sx={{ mb: { xs: 4, md: 0 } }}
                  onClick={() => navigate('/manage-autoshipments/template')}
                  startIcon={<SvgIcon><Plus fill="white" /></SvgIcon>}
                >
                  {t('newTemplateBtn')}
                </Button>
              </Box>
            )}
          </Stack>

          <Grid container
            sx={{ pt: '6px', pb: '12px' }}
            spacing={4}>
            <Grid item 
              xs={12}
              sm={6}
              md={3}>
              <TextField
                fullWidth
                size='small'
                label={t('searchByTemplate')}
                value={templateSearch}
                onChange={(e) => setTemplateSearch(e.target.value)}
              />
            </Grid>
            <Grid item
              xs={12}
              sm={6}
              md={3}>
              <TextField
                fullWidth
                size='small'
                label={t('orderType')}
                value={orderType}
                onChange={(e) => setOrderType(Number(e.target.value))}
                select
              >
                <MenuItem value="-1">{t('all')}</MenuItem>
                {orderTypes.map((type) => (
                  <MenuItem key={type.id}
                    value={type.id}>
                    {t(OrderTypeTranslationMap[type.name as keyof typeof OrderTypeTranslationMap]) || type.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item
              lg={2}
              sm={2}
              xs={12}>
              <Button
                variant="secondary"
                size="lg"
                onClick={clearAll}
                fullWidth
              >
                {t('clear')}
              </Button>
            </Grid>
            <Grid item
              lg={2}
              sm={2}
              xs={12}
              sx={{ display: 'flex', flexDirection: 'column', alignItems: 'right' }}>
              <Button
                variant="primary"
                size="lg"
                startIcon={<SvgIcon> <SearchSm /> </SvgIcon>}
                onClick={searchClick}
                fullWidth>
                {t('search')}
              </Button>
            </Grid>
          </Grid>

          <Grid item
            xs={12}>
            <Tabs value={activeTab}
              onChange={handleTabChange}>
              <Tab
                label={t('activeSheet')}
                value={AutoshipTemplateStatusEnum.ACTIVE} />
              <Tab
                label={t('archiveSheet')}
                value={AutoshipTemplateStatusEnum.ARCHIVED} />
            </Tabs>
          </Grid>

          <Grid item
            xs={12}
            sx={{ mt: '12px' }}>
            {!paginatedData || isLoading ? <LoadingWheel /> :
              <Section>
                {
                  <AutoshipmentTemplateTable
                    list={paginatedData}
                    activeTab={activeTab}
                    page={page}
                    setPage={setPage}
                    order={order}
                    setOrder={setOrder}
                    orderBy={orderBy}
                    setOrderBy={setOrderBy}
                    clearAll={clearAll}
                    navigateToTemplate={navigateToTemplate}
                    isLoading={isLoading}
                    orderTypes={orderTypes}
                    handleDelete={handleDelete}
                    handleArchive={handleArchive}
                    hasWritePermissions={hasWritePermissions}
                    hasDeletePermissions={hasDeletePermissions}
                  />
                }
              </Section >
            }
          </Grid>
        </Grid >
      </Box >
      <MessagesSnackbar 
        open={snackbarOpen}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        severity={snackbarSeverity}
        duration={5000} 
      />
    </PageArea >
  );
};

export default AutoshipmentTemplates;